'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SuperAdmin } from '@zcmss/types';
import { api } from '@/lib/api';
import { TOKEN_KEY } from '@/lib/api';

interface AuthState {
  token: string | null;
  user: SuperAdmin | null;
  setAuth: (token: string, user: SuperAdmin) => void;
  logout: () => Promise<void>;
  isAuthenticated: () => boolean;
}

function setAuthCookie(token: string) {
  document.cookie = `${TOKEN_KEY}=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
}

function clearAuthCookie() {
  document.cookie = `${TOKEN_KEY}=; path=/; max-age=0`;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,

      setAuth: (token, user) => {
        localStorage.setItem(TOKEN_KEY, token);
        setAuthCookie(token);
        set({ token, user });
      },

      logout: async () => {
        try {
          await api.superAdminLogout();
        } catch {
          // Ignore logout API errors — clear local session regardless
        }
        localStorage.removeItem(TOKEN_KEY);
        clearAuthCookie();
        set({ token: null, user: null });
      },

      isAuthenticated: () => !!get().token,
    }),
    {
      name: 'zcmss-admin-portal-auth',
      partialize: (state) => ({ token: state.token, user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          localStorage.setItem(TOKEN_KEY, state.token);
          setAuthCookie(state.token);
        }
      },
    }
  )
);
